import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button, Spinner } from 'flowbite-react';
import {
  HiUsers,
  HiOfficeBuilding,
  HiDeviceMobile,
  HiDocumentText,
  HiBell,
  HiPhone,
  HiCurrencyDollar,
  HiCog,
  HiLogout,
  HiHome,
  HiX,
  HiChevronLeft,
  HiChevronRight,
} from 'react-icons/hi';
import Header from './Header';

interface NavItem {
  title: string;
  href: string;
  icon: React.ReactNode;
}

const navItems: NavItem[] = [
  { title: 'Dashboard', href: '/dashboard', icon: <HiHome className="w-5 h-5" /> },
  { title: 'Usuarios', href: '/admin/users', icon: <HiUsers className="w-5 h-5" /> },
  { title: 'Tenants', href: '/admin/tenants', icon: <HiOfficeBuilding className="w-5 h-5" /> },
  { title: 'Dispositivos', href: '/admin/devices', icon: <HiDeviceMobile className="w-5 h-5" /> },
  { title: 'Logs', href: '/admin/logs', icon: <HiDocumentText className="w-5 h-5" /> },
  { title: 'Alertas', href: '/admin/alerts', icon: <HiBell className="w-5 h-5" /> },
  { title: 'Contactos', href: '/admin/contacts', icon: <HiPhone className="w-5 h-5" /> },
  { title: 'Precios', href: '/admin/prices', icon: <HiCurrencyDollar className="w-5 h-5" /> },
  { title: 'Configuración', href: '/admin/settings', icon: <HiCog className="w-5 h-5" /> },
];

export default function AdminLayout({ children }: { children: React.ReactNode }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { userProfile, signOut, refreshUserProfile } = useAuth();

  // Verificar que el usuario sea superadmin y esté activo
  useEffect(() => {
    if (userProfile) {
      if (userProfile.role !== 'superadmin') {
        navigate('/dashboard');
        return;
      }
      if (userProfile.status === 'inactive' && userProfile.role === 'superadmin') {
        console.warn('Superadmin con estado inactivo, refrescando perfil...');
        refreshUserProfile();
      }
    }
  }, [userProfile, navigate, refreshUserProfile]);

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  // No renderizar si el usuario no es superadmin
  if (userProfile && userProfile.role !== 'superadmin') {
    return null;
  }

  // Si no hay perfil aún, mostrar layout básico mientras carga
  if (!userProfile) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <Header onMenuClick={() => setSidebarOpen(!sidebarOpen)} />
        <div className="flex items-center justify-center min-h-[calc(100vh-4rem)]">
          <div className="text-center">
            <Spinner size="xl" className="mx-auto mb-4" />
            <p className="text-gray-600 dark:text-gray-300">Cargando perfil de usuario...</p>
          </div>
        </div>
      </div>
    );
  }

  const SidebarContent = ({ collapsed }: { collapsed: boolean }) => (
    <div className="flex flex-col h-full bg-white dark:bg-gray-800">
      {/* Header del Sidebar */}
      <div className={`flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 ${collapsed ? 'px-2' : ''}`}>
        {!collapsed && (
          <div className="flex-1 min-w-0">
            <h2 className="font-bold text-lg text-gray-900 dark:text-white">Domus Admin</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{userProfile?.email}</p>
          </div>
        )}
        {/* Botón colapsar/expandir - Solo desktop */}
        <Button
          color="light"
          size="sm"
          className="hidden lg:flex"
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          title={collapsed ? 'Expandir menú' : 'Colapsar menú'}
        >
          {collapsed ? <HiChevronRight className="w-4 h-4" /> : <HiChevronLeft className="w-4 h-4" />}
        </Button>
        {/* Botón cerrar para mobile */}
        <Button
          color="light"
          size="sm"
          className="lg:hidden"
          onClick={() => setSidebarOpen(false)}
        >
          <HiX className="w-5 h-5" />
        </Button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
        {navItems.map((item) => {
          const isActive = location.pathname === item.href;
          return (
            <Link
              key={item.href}
              to={item.href}
              onClick={() => setSidebarOpen(false)}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                collapsed ? 'justify-center' : ''
              } ${
                isActive
                  ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-200 font-semibold'
                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
              title={collapsed ? item.title : ''}
            >
              {item.icon}
              {!collapsed && <span className="text-sm whitespace-nowrap">{item.title}</span>}
            </Link>
          );
        })}
      </nav>

      {/* Footer - Solo en desktop, en mobile el logout está en el header dropdown */}
      {!collapsed && (
        <div className="hidden lg:block p-4 border-t border-gray-200 dark:border-gray-700">
          <Button
            onClick={handleSignOut}
            color="light"
            className="w-full justify-start gap-3 text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20"
          >
            <HiLogout className="w-5 h-5" />
            <span>Cerrar Sesión</span>
          </Button>
        </div>
      )}
    </div>
  );

  const toggleSidebar = () => {
    // En móvil, toggle del drawer
    if (window.innerWidth < 1024) {
      setSidebarOpen(!sidebarOpen);
    } else {
      // En desktop, toggle del colapso
      setSidebarCollapsed(!sidebarCollapsed);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Header onMenuClick={toggleSidebar} />

      <div className="flex">
        {/* Mobile Sidebar */}
        {sidebarOpen && (
          <div 
            className="lg:hidden fixed inset-0 z-40 bg-black bg-opacity-50" 
            onClick={() => setSidebarOpen(false)}
          >
            <div 
              className="fixed left-0 top-0 h-full w-80 bg-white dark:bg-gray-800 shadow-xl" 
              onClick={(e) => e.stopPropagation()}
            >
              <SidebarContent collapsed={false} />
            </div>
          </div>
        )}

        {/* Desktop Sidebar - Colapsable */}
        <aside className={`hidden lg:flex lg:flex-col lg:fixed lg:inset-y-0 lg:z-40 lg:pt-16 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 shadow-sm transition-all duration-300 ${
          sidebarCollapsed ? 'lg:w-16' : 'lg:w-64'
        }`}>
          <SidebarContent collapsed={sidebarCollapsed} />
        </aside>

        {/* Main Content */}
        <main className={`flex-1 pt-16 transition-all duration-300 ${
          sidebarCollapsed ? 'lg:pl-16' : 'lg:pl-64'
        }`}>
          <div className="p-4 lg:p-8">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}
